import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Gamepad2, Server, Cpu, Bot } from "lucide-react";
import { GAME_SERVER_PLANS, VPS_PLANS, DEDICATED_PLANS, BOT_PLANS } from "../data/plans";
import { PricingCard3D } from "./PricingCard3D";

const TABS = [
  { id: "gameservers", label: "Game Servers", icon: Gamepad2, plans: GAME_SERVER_PLANS, testId: "pricing-tab-game" },
  { id: "vps", label: "VPS", icon: Server, plans: VPS_PLANS, testId: "pricing-tab-vps" },
  { id: "dedicados", label: "Dedicados", icon: Cpu, plans: DEDICATED_PLANS, testId: "pricing-tab-dedicados" },
  { id: "bots", label: "Bots & Web", icon: Bot, plans: BOT_PLANS, testId: "pricing-tab-bots" },
];

export const PricingSection = () => {
  const [active, setActive] = useState("gameservers");
  const activeTab = TABS.find((t) => t.id === active);

  return (
    <section id="pricing" className="relative py-20 sm:py-24" data-testid="pricing-section">
      <div
        className="absolute inset-x-0 top-0 h-px"
        style={{ background: "linear-gradient(90deg, transparent, var(--osx-border-strong), transparent)" }}
        aria-hidden
      />
      <div className="osx-container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-12"
        >
          <div className="text-xs font-mono uppercase tracking-[0.25em] mb-3" style={{ color: "var(--osx-brand-600)" }}>
            Planes y Precios
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight" style={{ color: "var(--osx-text)" }}>
            Elegí el plan perfecto para <span className="osx-gradient-text">tu proyecto</span>
          </h2>
          <p className="mt-4 text-base sm:text-lg" style={{ color: "var(--osx-text-muted)" }}>
            Desde bots hasta servidores Bare Metal con Ryzen 9. Escalable, rápido y con todo lo que necesitas incluido.
          </p>
        </motion.div>

        {/* Tabs */}
        <div className="w-full flex justify-center mb-10">
        <div
          className="inline-flex flex-wrap items-center justify-center gap-1 p-1.5 rounded-2xl"
          style={{
            background: "var(--osx-surface)",
            border: "1px solid var(--osx-border-strong)",
            boxShadow: "inset 0 1px 2px rgba(2,132,199,0.06), var(--osx-shadow-1)",
          }}
          data-testid="pricing-tabs"
          role="tablist"
        >
          {TABS.map((tab) => {
            const isActive = active === tab.id;
            return (
              <button
                key={tab.id}
                role="tab"
                aria-selected={isActive}
                data-testid={tab.testId}
                onClick={() => setActive(tab.id)}
                className="relative inline-flex items-center gap-2 px-4 sm:px-5 py-2.5 rounded-xl text-sm font-semibold"
                style={{
                  color: isActive ? "#ffffff" : "var(--osx-text-muted)",
                  transition: "color 0.2s ease",
                  zIndex: 1,
                }}
              >
                {isActive && (
                  <motion.span
                    layoutId="pricing-tab-pill"
                    className="absolute inset-0 rounded-xl"
                    style={{
                      background: "linear-gradient(135deg, var(--osx-brand-600), var(--osx-accent-cyan))",
                      boxShadow: "0 8px 22px rgba(2,132,199,0.32)",
                      zIndex: -1,
                    }}
                    transition={{ type: "spring", stiffness: 260, damping: 28 }}
                  />
                )}
                <tab.icon size={15} strokeWidth={2.4} />
                {tab.label}
              </button>
            );
          })}
        </div>
        </div>

        {/* Cards */}
        <AnimatePresence mode="wait">
          <motion.div
            key={active}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.35 }}
            className="grid gap-5 sm:gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
          >
            {activeTab.plans.map((plan, idx) => (
              <PricingCard3D key={plan.id} plan={plan} index={idx} />
            ))}
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};
